import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Typography, Button, LinkHandler } from '@/components/atoms';
import { mapImgWithTextColors } from '@/utils/index';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { IImageWithTextCmp } from '../IImageWithTextCmp';
import { interpolation } from '@/utils/interpolation';

const Container = styled.div<IInfoContainer>`
  position: relative;
  line-height: 0;
  border-radius: ${props => props.theme.common.borderRadius};
  background-color: ${props => props.bgColor};
  display: flex;
  justify-content: center;
  text-align: center;
  padding: ${props =>
    props.template === 'banner_with_background_color'
      ? 'clamp(5rem, 3.598rem + 5.7516vw, 10.5rem) clamp(5rem, 3.598rem + 5.7516vw, 10.5rem)'
      : 'clamp(1rem, 1rem + 0vw, 1rem) clamp(1rem, 1rem + 0vw, 1rem)'};
`;

const HeadingContainer = styled.div<IHeading>`
  max-width: ${props =>
    props.template === 'banner_with_background_color' ? '800px' : '100%'};
`;
const CustomTypo = styled(Typography)``;

const DescContainer = styled.div<IDescContainer>`
  width: 100%;
  max-width: ${props =>
    props.maxWidthText ? `${props.maxWidthText}px` : '560px'};
  margin: auto;
  margin-top: clamp(1.5rem, 1.5rem + 0vw, 1.5rem);
`;

const LinkContainer = styled.div<ILinkContainer>`
  margin-top: ${props =>
    props.isMarginTopBig
      ? ' clamp(1.5rem, 0.9902rem + 2.0915vw, 3.5rem)'
      : 'clamp(1.5rem, 1.5rem + 0vw, 1.5rem)'}; //24px - 56px
`;

interface IInfoContainer {
  template?: string;
  noimage?: boolean;
  bgColor?: string;
}

interface IHeading {
  template?: string;
}

interface ILinkContainer {
  isMarginTopBig?: boolean;
}

interface IDescContainer {
  maxWidthText?: number;
  mbott?: boolean;
}

const BannerWithBackgroundColor: React.FC<IImageWithTextCmp> = props => {
  const {
    headingText,
    url,
    target,
    label,
    maxWidthText,
    template,
    desc,
    textColor,
    bgColor,
    ctaColor,
    borderColor,
    enableFont,
  } = props;

  const theme = useTheme();
  const { ctaColorMapped, textColorMapped } = mapImgWithTextColors(
    { borderColor, textColor, ctaColor },
    theme
  );

  const user = useSelector((state: RootState) => state.userReducer);
  const customerFirstName =
    user?.customer?.firstName || user?.profiling?.firstName || 'there';
  const customerCompany =
    user?.customer?.defaultAddress?.company || user?.profiling?.shopName || '';

  const infoContent = (
    <div>
      <HeadingContainer template={template}>
        {headingText ? (
          <CustomTypo
            datatype="heading_1"
            textcolor={textColorMapped}
            component={'h2'}
            dangerouslySetInnerHTML={{
              __html: interpolation(headingText, {
                customerName: `<span>${customerFirstName}</span>`,
                storeName: `<span>${customerCompany}</span>`,
              }),
            }}
            className={enableFont ? 'titlebig' : ''}
          />
        ) : null}
        {desc ? (
          <DescContainer
            maxWidthText={maxWidthText}
            mbott={url !== '' ? true : false}>
            <Typography datatype="subtitle_2" textcolor={textColorMapped}>
              {desc}
            </Typography>
          </DescContainer>
        ) : null}
      </HeadingContainer>
      {url && (
        <LinkContainer isMarginTopBig={desc ? true : false}>
          <LinkHandler href={url} passHref source={`cmsLink-iwtb-${template}`}>
            <a target={target}>
              <Button variant={'text'} color={ctaColorMapped}>
                {label}
              </Button>
            </a>
          </LinkHandler>
        </LinkContainer>
      )}
    </div>
  );

  return (
    <Container bgColor={bgColor} template={template}>
      {infoContent}
    </Container>
  );
};

export default BannerWithBackgroundColor;
